/**
 * Decode and classify the SIGNED calldata of an EVM vault proposal
 * (ENTX-A-1 / ENTX-A-5, contract "EVM call-safety rules").
 *
 * What the signers authorise is `evmUserOp.callData` — the smart account's
 * `execute(dest, value, func)` or `executeBatch(dest[], func[])`. A proposal's
 * `customData`, `recipients`, `tokenContract` etc. are only metadata stored next
 * to it, so every "what does this do" view must be derived from the signed
 * bytes, and the metadata is checked against them.
 *
 * Rules (same as the wallet, Key and relay builder):
 *  - any non-empty inner calldata is a contract call, whatever the value;
 *  - a call whose target is the account itself, or any call with the
 *    `upgradeToAndCall` / `upgradeTo` selector (to any target), is BLOCKED;
 *  - `executeBatch` is decoded call by call with the same rules;
 *  - calldata that can't be decoded is an error — callers must fail closed.
 *
 * Pure, dependency-free and never throws.
 */

export const EXECUTE_SELECTOR = '0xb61d27f6'; // execute(address,uint256,bytes)
export const EXECUTE_BATCH_SELECTOR = '0x18dfb3c7'; // executeBatch(address[],bytes[])

const UPGRADE_SELECTORS: Record<string, string> = {
  '0x4f1ef286': 'upgradeToAndCall(address,bytes)',
  '0x3659cfe6': 'upgradeTo(address)',
};

export type EvmInnerCallKind =
  | 'native_transfer'
  | 'erc20_transfer'
  | 'erc20_transfer_from'
  | 'erc20_approve'
  | 'erc20_increase_allowance'
  | 'set_approval_for_all'
  | 'erc20_permit'
  | 'upgrade'
  | 'self_call'
  | 'contract_call';

export type EvmCallRisk = 'none' | 'warning' | 'danger';

export interface EvmInnerCall {
  index: number;
  /** Lower-cased 0x address. */
  target: string;
  /** Native value in wei (decimal string). */
  value: string;
  /** 0x-prefixed inner calldata ('0x' when empty). */
  data: string;
  /** 0x + 4-byte selector, or null for empty calldata. */
  selector: string | null;
  kind: EvmInnerCallKind;
  /** Human signature of the decoded method, when known. */
  method?: string;
  /** Decoded arguments for known methods (addresses lower-cased, uints decimal). */
  args?: Record<string, string>;
  /** Approve-type calls: allowance is effectively unlimited. */
  unlimited?: boolean;
  /** Self-calls and upgrades: never sign. */
  blocked: boolean;
  risk: EvmCallRisk;
}

export type SignedEvmCallAnalysis =
  | {
      ok: true;
      wrapper: 'execute' | 'executeBatch';
      /** Lower-cased smart-account address from the userOp. */
      sender: string;
      calls: EvmInnerCall[];
      /** At least one call is blocked (self-call / upgrade). */
      blocked: boolean;
      /** At least one call carries non-empty calldata. */
      hasContractInteraction: boolean;
    }
  | { ok: false; error: EvmDecodeError };

export type EvmDecodeError =
  | 'MISSING_CALLDATA'
  | 'INVALID_SENDER'
  | 'INVALID_HEX'
  | 'UNKNOWN_WRAPPER'
  | 'MALFORMED_ABI';

const MAX_UINT256 = (1n << 256n) - 1n;
/** Approvals at or above 2^255 are treated as "unlimited" (covers MAX_UINT256 and friends). */
const UNLIMITED_THRESHOLD = 1n << 255n;
/** Hard cap on batch size we are willing to render (the builder never batches). */
const MAX_BATCH_CALLS = 64;

class AbiError extends Error {}

function strip0x(hex: string): string {
  return hex.startsWith('0x') || hex.startsWith('0X') ? hex.slice(2) : hex;
}

/** The 32-byte word of `hex` that starts at byte offset `byteOffset`. */
function readWord(hex: string, byteOffset: number): string {
  const start = byteOffset * 2;
  const word = hex.slice(start, start + 64);
  if (word.length !== 64) throw new AbiError('out of bounds');
  return word;
}

function wordToBigInt(word: string): bigint {
  return BigInt('0x' + word);
}

function wordToSafeNumber(word: string): number {
  const value = wordToBigInt(word);
  if (value > BigInt(Number.MAX_SAFE_INTEGER)) throw new AbiError('offset too large');
  return Number(value);
}

function wordToAddress(word: string): string {
  // The 12 high bytes of an ABI-encoded address must be zero.
  if (!/^0{24}/.test(word)) throw new AbiError('dirty address');
  return '0x' + word.slice(24).toLowerCase();
}

/** Read `bytes` whose length word sits at byte offset `at`. */
function readBytes(hex: string, at: number): string {
  const length = wordToSafeNumber(readWord(hex, at));
  const start = (at + 32) * 2;
  const data = hex.slice(start, start + length * 2);
  if (data.length !== length * 2) throw new AbiError('bytes out of bounds');
  return data;
}

function isAddress(value: unknown): value is string {
  return typeof value === 'string' && /^0x[0-9a-fA-F]{40}$/.test(value);
}

/** Decode a known inner call; returns the classification for `data`. */
function classifyInner(
  index: number,
  target: string,
  value: bigint,
  dataHex: string,
  sender: string
): EvmInnerCall {
  const data = '0x' + dataHex.toLowerCase();
  const selector = dataHex.length >= 8 ? '0x' + dataHex.slice(0, 8).toLowerCase() : null;
  const base = { index, target, value: value.toString(), data: dataHex ? data : '0x', selector };

  // Upgrade selectors are refused whatever the target.
  if (selector && UPGRADE_SELECTORS[selector]) {
    return {
      ...base,
      kind: 'upgrade',
      method: UPGRADE_SELECTORS[selector],
      blocked: true,
      risk: 'danger',
    };
  }
  // A call into the account itself can rewrite owners, upgrade, withdraw deposits…
  if (target === sender) {
    return { ...base, kind: 'self_call', blocked: true, risk: 'danger' };
  }
  if (!dataHex) {
    return { ...base, kind: 'native_transfer', blocked: false, risk: 'none' };
  }
  if (dataHex.length % 2 !== 0 || selector === null) {
    return { ...base, kind: 'contract_call', blocked: false, risk: 'warning' };
  }

  const params = dataHex.slice(8);
  try {
    switch (selector) {
      case '0xa9059cbb': // transfer(address,uint256)
        return {
          ...base,
          kind: 'erc20_transfer',
          method: 'transfer(address,uint256)',
          args: {
            to: wordToAddress(readWord(params, 0)),
            amount: wordToBigInt(readWord(params, 32)).toString(),
          },
          blocked: false,
          risk: 'none',
        };
      case '0x23b872dd': // transferFrom(address,address,uint256)
        return {
          ...base,
          kind: 'erc20_transfer_from',
          method: 'transferFrom(address,address,uint256)',
          args: {
            from: wordToAddress(readWord(params, 0)),
            to: wordToAddress(readWord(params, 32)),
            amount: wordToBigInt(readWord(params, 64)).toString(),
          },
          blocked: false,
          risk: 'warning',
        };
      case '0x095ea7b3': // approve(address,uint256)
      case '0x39509351': {
        // increaseAllowance(address,uint256)
        const amount = wordToBigInt(readWord(params, 32));
        return {
          ...base,
          kind: selector === '0x095ea7b3' ? 'erc20_approve' : 'erc20_increase_allowance',
          method:
            selector === '0x095ea7b3'
              ? 'approve(address,uint256)'
              : 'increaseAllowance(address,uint256)',
          args: { spender: wordToAddress(readWord(params, 0)), amount: amount.toString() },
          unlimited: amount >= UNLIMITED_THRESHOLD,
          blocked: false,
          risk: 'danger',
        };
      }
      case '0xa22cb465': {
        // setApprovalForAll(address,bool)
        const approved = wordToBigInt(readWord(params, 32));
        return {
          ...base,
          kind: 'set_approval_for_all',
          method: 'setApprovalForAll(address,bool)',
          args: {
            operator: wordToAddress(readWord(params, 0)),
            approved: approved === 0n ? 'false' : 'true',
          },
          blocked: false,
          risk: approved === 0n ? 'warning' : 'danger',
        };
      }
      case '0xd505accf': // permit(address,address,uint256,uint256,uint8,bytes32,bytes32)
        return {
          ...base,
          kind: 'erc20_permit',
          method: 'permit(address,address,uint256,uint256,uint8,bytes32,bytes32)',
          args: {
            owner: wordToAddress(readWord(params, 0)),
            spender: wordToAddress(readWord(params, 32)),
            amount: wordToBigInt(readWord(params, 64)).toString(),
            deadline: wordToBigInt(readWord(params, 96)).toString(),
          },
          blocked: false,
          risk: 'danger',
        };
      default:
        return { ...base, kind: 'contract_call', blocked: false, risk: 'warning' };
    }
  } catch {
    // Known selector but malformed arguments: still a contract call — and an
    // unreadable one, so it keeps a warning rather than a friendly label.
    return { ...base, kind: 'contract_call', blocked: false, risk: 'warning' };
  }
}

/**
 * Classify a single call before it becomes a proposal (e.g. a dApp's
 * eth_sendTransaction). Same rules as the signed-call decode; null when the
 * target/sender/value/data are malformed (callers refuse the request).
 */
export function classifyEvmCall(params: {
  target: unknown;
  value: unknown;
  data: unknown;
  sender: unknown;
}): EvmInnerCall | null {
  if (!isAddress(params.target) || !isAddress(params.sender)) return null;
  let value: bigint;
  try {
    value =
      params.value === undefined || params.value === null || params.value === ''
        ? 0n
        : BigInt(params.value as string);
  } catch {
    return null;
  }
  if (value < 0n || value > MAX_UINT256) return null;
  if (params.data !== undefined && params.data !== null && typeof params.data !== 'string') {
    return null;
  }
  const dataHex = typeof params.data === 'string' ? strip0x(params.data) : '';
  if (!/^[0-9a-fA-F]*$/.test(dataHex) || dataHex.length % 2 !== 0) return null;
  return classifyInner(
    0,
    params.target.toLowerCase(),
    value,
    dataHex.toLowerCase(),
    params.sender.toLowerCase()
  );
}

/**
 * Decode `evmUserOp.callData` (the signed object) into its inner calls.
 * `userOp` is the proposal's `evmUserOp`.
 */
export function analyzeSignedEvmUserOp(
  userOp: Record<string, string> | undefined | null
): SignedEvmCallAnalysis {
  const callData = userOp?.['callData'];
  if (typeof callData !== 'string' || strip0x(callData).length === 0) {
    return { ok: false, error: 'MISSING_CALLDATA' };
  }
  const sender = userOp?.['sender'];
  if (!isAddress(sender)) return { ok: false, error: 'INVALID_SENDER' };
  const senderLc = sender.toLowerCase();

  const hex = strip0x(callData).toLowerCase();
  if (!/^[0-9a-f]*$/.test(hex) || hex.length % 2 !== 0) {
    return { ok: false, error: 'INVALID_HEX' };
  }
  const selector = '0x' + hex.slice(0, 8);
  const params = hex.slice(8);

  try {
    let wrapper: 'execute' | 'executeBatch';
    const calls: EvmInnerCall[] = [];
    if (selector === EXECUTE_SELECTOR) {
      wrapper = 'execute';
      const target = wordToAddress(readWord(params, 0));
      const value = wordToBigInt(readWord(params, 32));
      if (value > MAX_UINT256) throw new AbiError('value');
      const dataOffset = wordToSafeNumber(readWord(params, 64));
      const data = readBytes(params, dataOffset);
      calls.push(classifyInner(0, target, value, data, senderLc));
    } else if (selector === EXECUTE_BATCH_SELECTOR) {
      wrapper = 'executeBatch';
      const destOffset = wordToSafeNumber(readWord(params, 0));
      const funcOffset = wordToSafeNumber(readWord(params, 32));
      const destCount = wordToSafeNumber(readWord(params, destOffset));
      const funcCount = wordToSafeNumber(readWord(params, funcOffset));
      // The contract reverts on mismatched lengths; refuse rather than guess.
      if (destCount !== funcCount) throw new AbiError('length mismatch');
      if (destCount === 0 || destCount > MAX_BATCH_CALLS) throw new AbiError('batch size');
      const funcBase = funcOffset + 32;
      for (let i = 0; i < destCount; i++) {
        const target = wordToAddress(readWord(params, destOffset + 32 + i * 32));
        const elementOffset = wordToSafeNumber(readWord(params, funcBase + i * 32));
        const data = readBytes(params, funcBase + elementOffset);
        // executeBatch always forwards value 0.
        calls.push(classifyInner(i, target, 0n, data, senderLc));
      }
    } else {
      return { ok: false, error: 'UNKNOWN_WRAPPER' };
    }

    return {
      ok: true,
      wrapper,
      sender: senderLc,
      calls,
      blocked: calls.some((c) => c.blocked),
      hasContractInteraction: calls.some((c) => c.data !== '0x'),
    };
  } catch {
    return { ok: false, error: 'MALFORMED_ABI' };
  }
}

/** What the proposal's metadata claims, for comparison with the signed calls. */
export interface EvmProposalClaim {
  recipients: ReadonlyArray<{ address: string; amount: string }>;
  tokenContract?: string;
  customData?: string;
  /** Source vault address shown on the page, when known. */
  sourceAddress?: string;
}

export type EvmMetadataIssue =
  /** The signed object holds a different number of calls than the metadata describes. */
  | 'CALL_COUNT'
  /** The signed call goes to a different contract/recipient than displayed. */
  | 'TARGET'
  /** The signed amount differs from the displayed amount. */
  | 'AMOUNT'
  /** A token proposal whose signed call is not a plain ERC-20 transfer. */
  | 'NOT_A_TOKEN_TRANSFER'
  /** A token proposal that also sends native value. */
  | 'UNEXPECTED_VALUE'
  /** Native proposal whose signed calldata differs from the proposal's customData. */
  | 'CALLDATA'
  /** The UserOperation spends from a different account than the displayed source. */
  | 'SENDER';

function sameAddress(a: string | undefined, b: string | undefined): boolean {
  return !!a && !!b && a.toLowerCase() === b.toLowerCase();
}

function sameAmount(signed: string, claimed: string): boolean {
  try {
    return BigInt(signed) === BigInt(claimed);
  } catch {
    return false;
  }
}

/**
 * Compare the displayed proposal metadata with the decoded signed calls.
 * Any issue means the screen would describe something other than what is
 * signed — callers must refuse to sign or approve.
 */
export function compareEvmProposalToSignedCalls(
  claim: EvmProposalClaim,
  analysis: Extract<SignedEvmCallAnalysis, { ok: true }>
): EvmMetadataIssue[] {
  const issues = new Set<EvmMetadataIssue>();
  if (claim.sourceAddress && !sameAddress(analysis.sender, claim.sourceAddress)) {
    issues.add('SENDER');
  }
  const call = analysis.calls[0];
  const recipient = claim.recipients[0];
  if (analysis.calls.length !== 1 || claim.recipients.length !== 1 || !call || !recipient) {
    issues.add('CALL_COUNT');
    return Array.from(issues);
  }

  if (claim.tokenContract) {
    if (!sameAddress(call.target, claim.tokenContract)) issues.add('TARGET');
    if (call.value !== '0') issues.add('UNEXPECTED_VALUE');
    if (call.kind !== 'erc20_transfer' || !call.args) {
      issues.add('NOT_A_TOKEN_TRANSFER');
    } else {
      if (!sameAddress(call.args['to'], recipient.address)) issues.add('TARGET');
      if (!sameAmount(call.args['amount'] ?? '', recipient.amount)) issues.add('AMOUNT');
    }
  } else {
    if (!sameAddress(call.target, recipient.address)) issues.add('TARGET');
    if (!sameAmount(call.value, recipient.amount)) issues.add('AMOUNT');
    const claimedData = claim.customData && claim.customData !== '0x' ? claim.customData : '0x';
    if (call.data.toLowerCase() !== claimedData.toLowerCase()) issues.add('CALLDATA');
  }
  return Array.from(issues);
}
